import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { compareTariffApis } from "./enum";
import moment from "moment";

const HsCodeAutosuggest = ({ isDisabled, country, handleChange, selectedItem, tradeDirection, date = new Date() }) => {
  const [inputValue, setInputValue] = useState("");
  const [suggestionsData, setSuggestionsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const debounceTimerRef = useRef(null);
  const containerRef = useRef(null);
  const suggestionsRef = useRef(null);
  const cancelTokenRef = useRef(null);

  useEffect(() => {
    if (selectedItem?.suggest) {
      setInputValue(selectedItem.suggest);
    } else {
      setInputValue("");
    }
  }, [selectedItem]);

  useEffect(() => {
    return () => {
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel("Component unmounted");
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const debounce = (func, delay) => {
    return (...args) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  const suggestTaxableHscodes = async (searchedText = "") => {
    if (searchedText.length < 2) {
      setSuggestionsData([]);
      return;
    }

    try {
      setIsLoading(true);
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel("New request initiated");
      }

      cancelTokenRef.current = axios.CancelToken.source();

      const params = {
        ...(tradeDirection === "import" ? { countryOfImport: country } : { countryOfExport: country }),
        search: searchedText,
        tradeDirection,
        effectiveDate: moment(date).format("YYYY-MM-DD"),
      };

      const { data } = await axios.post(compareTariffApis.landedcostAutosuggest, params, { cancelToken: cancelTokenRef.current.token });

      if (data?.suggestions?.length > 0) {
        setSuggestionsData(data.suggestions);
        setIsDropdownOpen(true);
        setHighlightedIndex(0);
      } else {
        setSuggestionsData([]);
      }
    } catch (err) {
      if (!axios.isCancel(err)) {
        toast.error(err?.response?.data?.message ?? "Failed to load suggestions");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedFetchSuggestions = debounce(suggestTaxableHscodes, 800);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedFetchSuggestions(value);
    setSuggestionsData([]);
  };

  const handleSuggestionSelect = (item) => {
    handleChange(item);
    setIsDropdownOpen(false);
    setHighlightedIndex(-1);
  };

  const handleClear = () => {
    setInputValue("");
    setSuggestionsData([]);
    setIsDropdownOpen(false);
    setHighlightedIndex(-1);
    handleChange(null);

    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel("Input cleared");
      cancelTokenRef.current = null;
    }
  };

  const handleInputFocus = () => {
    if (isDisabled) return;
    setIsDropdownOpen(true);
    if (inputValue) suggestTaxableHscodes(inputValue);
  };

  const handleKeyDown = (e) => {
    if (!isDropdownOpen || !suggestionsData.length) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < suggestionsData.length - 1 ? prev + 1 : prev));
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
      case "Tab":
        e.preventDefault();
        if (highlightedIndex >= 0 && suggestionsData[highlightedIndex]) {
          handleSuggestionSelect(suggestionsData[highlightedIndex]);
        }
        break;
      case "Escape":
        setIsDropdownOpen(false);
        break;
      default:
        return;
    }
  };

  useEffect(() => {
    if (highlightedIndex >= 0 && suggestionsRef.current) {
      const highlightedElement = suggestionsRef.current.children[highlightedIndex];
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, [highlightedIndex]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        if (!selectedItem) {
          handleClear();
        } else {
          setInputValue(selectedItem.suggest);
          setIsDropdownOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedItem]);

  return (
    <div ref={containerRef} className="tw-relative tw-w-full">
      <div className={`tw-relative tw-flex tw-items-center tw-h-11 tw-w-full tw-border tw-rounded-[8px] tw-px-2 ${isDisabled ? "tw-bg-gray-100" : ""}`}>
        <input type="text" placeholder="Type Product Keyword or HS Code" className="tw-flex-1 tw-py-2 tw-text-left tw-text-sm tw-font-medium tw-border-none tw-outline-none disabled:tw-bg-gray-100 disabled:tw-cursor-not-allowed" value={inputValue} onChange={handleInputChange} onFocus={handleInputFocus} onKeyDown={handleKeyDown} disabled={isDisabled} />

        {inputValue && (
          <span onClick={handleClear} className="tw-ml-2 tw-cursor-pointer tw-text-gray-400 hover:tw-text-gray-600">
            ✕
          </span>
        )}

        <svg fill="#102828" className="tw-ml-2 tw-cursor-pointer" height="24" width="24" viewBox="0 0 20 20" aria-hidden="true" focusable="false" onClick={handleInputFocus}>
          <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"></path>
        </svg>
      </div>

      {isDropdownOpen && (
        <div ref={suggestionsRef} className="tw-absolute tw-left-0 tw-right-0 tw-mt-1 tw-bg-white tw-border tw-rounded-[8px] tw-shadow-lg tw-max-h-[200px] tw-overflow-y-auto tw-p-2 tw-z-20">
          {isLoading ? (
            <div className="tw-px-2 tw-py-3 tw-text-sm tw-text-gray-500">Loading...</div>
          ) : inputValue.length < 2 ? (
            <div className="tw-px-2 tw-py-3 tw-text-sm tw-text-gray-500">Please enter at least 2 characters</div>
          ) : !suggestionsData.length ? (
            <div className="tw-px-2 tw-py-3 tw-text-sm tw-text-gray-500">Please enter a valid HS Code</div>
          ) : (
            suggestionsData.map((item, index) => (
              <div key={index} onClick={() => handleSuggestionSelect(item)} onMouseEnter={() => setHighlightedIndex(index)} className={`tw-cursor-pointer tw-rounded-md tw-text-sm tw-mb-1 tw-p-1.5 ${selectedItem?.hscode === item.hscode ? "tw-bg-primary-purple-100" : highlightedIndex === index ? "tw-bg-gray-200" : ""}`}>
                {item.suggest}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default HsCodeAutosuggest;
